import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import DataTable from "PFComponents/data_table/data_table";
import { LoadingDots } from "PFComponents/loading_dots";
import InputFieldSet from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import { GROWL_ACTIONS } from "PFCore/reducers/growl_reducer";
import { ApiRoute } from "PFCore/utilities/routes";
import AdminListPage from "PFModules/admin/components/admin_list_page";
import React, { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import css from "./rss_page.less";

const RssPage = () => {
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation("admin", { keyPrefix: "rssFeed" });
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [itemToRemove, setItemToRemove] = useState(null);

  const refetch = () => {
    const args = { api_version: 2, url: ApiRoute("/api/feeds?per_page=1000") };
    $.ajax(args).then((resp) => setItems(resp.entries.concat({})));
  };

  const getErrorsFromResp = (errors, itemId, resp) => {
    const error = {};

    if (resp.responseJSON.errors && resp.responseJSON.errors.base) {
      const type = GROWL_ACTIONS.GROWL_RENDER;
      dispatch({ type, payload: { message: resp.responseJSON.errors.base[0] || "", kind: "error" } });
    } else if (resp.responseJSON.errors && resp.responseJSON.errors.forEach) {
      resp.responseJSON.errors.forEach(({ source, detail }) => {
        const key = source.pointer.replace(/.*\//, "");
        error[key] = detail;
      });
    }

    return { ...errors, [itemId || "NEW"]: error };
  };

  const itemToData = (item) => ({
    name: (item.name || "").trim(),
    url: (item.url || "").trim(),
    autocreate_posts: !!item.autocreate_posts
  });

  const create = (item) => {
    setErrors((errors) => {
      errors[item.id || "NEW"] = {};
      return { ...errors };
    });

    $.ajax({
      method: "POST",
      api_version: 2,
      url: ApiRoute("/api/feeds"),
      data: itemToData(item)
    })
      .then((resp) =>
        setItems((items) => {
          items[items.length - 1] = resp;
          return [...items, {}];
        })
      )
      .catch((resp) => setErrors((errors) => getErrorsFromResp(errors, item.id, resp)));
  };

  const save = (item) => {
    setErrors((errors) => {
      errors[item.id || "NEW"] = {};
      return { ...errors };
    });

    $.ajax({
      method: "PUT",
      api_version: 2,
      url: ApiRoute(`/api/feeds${item.id ? `/${item.id}` : ""}`),
      data: itemToData(item)
    })
      .then((resp) => {
        Object.keys(resp, (key, val) => (item[key] = val));
        delete item.changed;
        setItems((items) => [...items]);
      })
      .catch((resp) => setErrors((errors) => getErrorsFromResp(errors, item.id, resp)));
  };

  const remove = (item) => {
    $.ajax({
      method: "DELETE",
      api_version: 2,
      url: ApiRoute(`/api/feeds/${item.id}`)
    }).then(() => {
      setItems((items) => [...items.filter(({ id }) => id !== item.id)]);
    });
  };

  useEffect(() => {
    refetch();
  }, []);

  const onChange = (item, key, value) => {
    item[key] = value;
    item.changed = (item.changed || 0) + 1;
    setItems((items) => [...items]);
  };
  // eslint-disable-next-line react/display-name
  const renderCell = (key) => (item) =>
    (
      <InputFieldSet
        handleChange={(value) => onChange(item, key, value)}
        value={item[key] || ""}
        innerStyle={{ border: "1px solid #ddd" }}
        error={errors[item.id || "NEW"] && errors[item.id || "NEW"][key]}
        maxLength={255}
        minVisibleMaxLengthFactor={2}
        bareInput
      />
    );

  return (
    <div className={css.root}>
      {itemToRemove && (
        <ConfirmModal handleOK={() => remove(itemToRemove)} handleClose={() => setItemToRemove(null)}>
          {itemToRemove.name ? (
            <Trans
              t={t}
              i18nKey="areYouSureYouWantToRemoveRssFeed"
              components={[<i style={{ overflowWrap: "break-word" }} key={0} />]}
              values={{ name: itemToRemove.name }}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="areYouSureYouWantToRemoveThisRssFeed"
              components={[<i style={{ overflowWrap: "break-word" }} key={0} />]}
            />
          )}
        </ConfirmModal>
      )}
      <AdminListPage title={t("title")}>
        <DataTable
          qaId="DataTable.RssPage"
          data={items}
          columns={[
            {
              headerName: t("name"),
              renderCell: renderCell("name"),
              tdStyle: { width: "50%" }
            },
            {
              headerName: t("url"),
              renderCell: renderCell("url"),
              tdStyle: { width: "50%" }
            },
            {
              headerName: t("csr"),
              narrow: true,
              renderCell: (item) => (
                <Toggle
                  checked={!!item.autocreate_posts}
                  onChange={(checked) => onChange(item, "autocreate_posts", checked)}
                />
              )
            },
            {
              headerName: t("actions"),
              narrow: true,
              renderCell: (item) => {
                const empty = !(item.name || "").trim() || !(item.url || "").trim();
                return (
                  <div className={css.actions}>
                    {item.id && (
                      <Button
                        small
                        disabled={!item.changed || empty}
                        kind={!item.changed || empty ? "tertiary" : "secondary"}
                        onClick={() => save(item)}
                      >
                        {t("translation:save")}
                      </Button>
                    )}
                    {item.id && (
                      <Button
                        text={t("translation:remove")}
                        small
                        kind="danger"
                        onClick={() => setItemToRemove(item)}
                      />
                    )}
                    {!item.id && (
                      <Button
                        small
                        disabled={empty}
                        kind={empty ? "tertiary" : "secondary"}
                        onClick={() => create(item)}
                      >
                        {t("translation:create")}
                      </Button>
                    )}
                  </div>
                );
              }
            }
          ]}
        />
        {items.length === 0 && <LoadingDots circlesEnabled pageViewEnabled />}
      </AdminListPage>
    </div>
  );
};

export default RssPage;
